import { calculateImportedSvgContentBounds } from '../objects/imported_svg_placement_metadata.mjs';

export const COMMON_IMAGE_LIMITS = Object.freeze({sourceBytes:8*1024*1024,sourcePixels:32*1024*1024,edge:4096,svgEdge:1024,pixels:16*1024*1024,images:32});
const check=(value,message)=>{if(!value)throw new Error(`Common image: ${message}`);};
export function rasterDimensions(bytes) {
  const v=new DataView(bytes.buffer,bytes.byteOffset,bytes.byteLength);
  if(bytes.length>=24&&v.getUint32(0)===0x89504e47&&v.getUint32(4)===0x0d0a1a0a)return {width:v.getUint32(16),height:v.getUint32(20),type:'image/png'};
  if(bytes[0]===255&&bytes[1]===216){
    let p=2;
    while(p+4<=bytes.length){
      if(bytes[p++]!==255)break;
      while(bytes[p]===255)p++;
      const marker=bytes[p++];if(marker===0xd9||marker===0xda)break;
      if(marker>=0xd0&&marker<=0xd7)continue;
      if(p+2>bytes.length)break;
      const length=v.getUint16(p);if(length<2||p+length>bytes.length)break;
      if([0xc0,0xc1,0xc2].includes(marker)&&length>=8)return {width:v.getUint16(p+5),height:v.getUint16(p+3),type:'image/jpeg'};
      p+=length;
    }
  }
  throw new Error('Common image: PNG/JPEG raster required');
}
/** SVG stays detached and is decoded once as an image, never installed into the live plan DOM. */
export function prepareCommonSvg(text) {
  const doc=new DOMParser().parseFromString(text,'image/svg+xml'),root=doc.documentElement;
  check(root?.localName==='svg'&&!doc.querySelector('parsererror'),'invalid SVG');
  check(!doc.querySelector('script,foreignObject,image,feImage,animate,animateTransform,set'),'unsupported SVG content');
  const elements=[root,...root.querySelectorAll('*')];check(elements.length<=20000,'SVG element budget');
  for(const node of elements)for(const attr of node.attributes){
    check(!/^on/i.test(attr.name),'SVG event attribute');
    if(attr.localName==='href')check(attr.value.startsWith('#'),'external SVG reference');
  }
  check(!/@import|@font-face/i.test(text),'external SVG style');
  for(const match of text.matchAll(/url\(\s*['"]?([^)'"\s]+)/gi))check(match[1].startsWith('#'),'external SVG resource');
  const bounds=calculateImportedSvgContentBounds(root);
  check(bounds&&bounds.width>0&&bounds.depth>0,'SVG content bounds unavailable');
  const ratio=COMMON_IMAGE_LIMITS.svgEdge/Math.max(bounds.width,bounds.depth);
  const width=Math.max(1,Math.round(bounds.width*ratio)),height=Math.max(1,Math.round(bounds.depth*ratio));
  root.setAttribute('viewBox',`${bounds.minX} ${bounds.minZ} ${bounds.width} ${bounds.depth}`);
  root.setAttribute('width',String(width));root.setAttribute('height',String(height));root.setAttribute('preserveAspectRatio','none');
  return {blob:new Blob([new XMLSerializer().serializeToString(root)],{type:'image/svg+xml'}),width,height};
}
export async function loadCommonPlanImage(image,{signal}={}) {
  const source=String(image.source||'');
  check(source.length<=Math.ceil(COMMON_IMAGE_LIMITS.sourceBytes*1.4),'source URL budget');
  const url=new URL(source,location.href);
  check(url.protocol==='data:'||url.origin===location.origin&&['http:','https:'].includes(url.protocol),'same-origin or embedded image required');
  let bytes;
  if(url.protocol==='data:'){
    const comma=source.indexOf(','),header=source.slice(0,comma),payload=source.slice(comma+1);
    check(/^data:image\/(png|jpeg|svg\+xml)(;base64)?$/i.test(header),'embedded image type');
    bytes=header.endsWith(';base64')?Uint8Array.from(atob(payload),c=>c.charCodeAt(0)):new TextEncoder().encode(decodeURIComponent(payload));
  }else{
    const response=await fetch(source,{signal});check(response.ok,'source unavailable');
    const reader=response.body.getReader(),chunks=[];let length=0;
    try{while(true){const {done,value}=await reader.read();if(done)break;
      length+=value.byteLength;check(length<=COMMON_IMAGE_LIMITS.sourceBytes,'source byte budget');chunks.push(value);
    }}catch(error){await reader.cancel();throw error;}finally{reader.releaseLock();}
    bytes=new Uint8Array(length);let offset=0;for(const chunk of chunks){bytes.set(chunk,offset);offset+=chunk.length;}
  }
  check(bytes.byteLength<=COMMON_IMAGE_LIMITS.sourceBytes,'source byte budget');
  let spec;
  if(image.format==='svg')spec=prepareCommonSvg(new TextDecoder().decode(bytes));
  else {
    const size=rasterDimensions(new Uint8Array(bytes));
    check(size.width>0&&size.height>0&&size.width*size.height<=COMMON_IMAGE_LIMITS.sourcePixels,'decoded pixel budget');
    const ratio=Math.min(1,COMMON_IMAGE_LIMITS.edge/Math.max(size.width,size.height));
    spec={width:Math.max(1,Math.round(size.width*ratio)),height:Math.max(1,Math.round(size.height*ratio)),blob:new Blob([bytes],{type:size.type})};
  }
  const objectUrl=URL.createObjectURL(spec.blob),element=new Image();
  try{
    element.src=objectUrl;await element.decode();if(signal?.aborted)throw new Error('Image load cancelled');
    const canvas=document.createElement('canvas');canvas.width=spec.width;canvas.height=spec.height;
    const context=canvas.getContext('2d',{willReadFrequently:true});check(context,'2D image conversion unavailable');
    context.drawImage(element,0,0,spec.width,spec.height);
    // CPU alpha sample also supplies silhouette selection, with no GPU readback during input.
    const alphaCanvas=document.createElement('canvas');alphaCanvas.width=alphaCanvas.height=128;
    const alphaContext=alphaCanvas.getContext('2d',{willReadFrequently:true});alphaContext.drawImage(canvas,0,0,128,128);
    const rgba=alphaContext.getImageData(0,0,128,128).data,alpha=new Uint8Array(128*128);
    for(let i=0;i<alpha.length;i++)alpha[i]=rgba[i*4+3];alphaCanvas.width=alphaCanvas.height=1;
    return {canvas,alpha,pixels:spec.width*spec.height};
  }finally{element.src='';URL.revokeObjectURL(objectUrl);}
}
export function hitCommonImage(record,point,alpha) {
  const [a,b,,d]=record.points,ux=b.x-a.x,uz=b.z-a.z,vx=d.x-a.x,vz=d.z-a.z;
  const determinant=ux*vz-uz*vx;if(!determinant)return false;
  const x=point.x-a.x,z=point.z-a.z,u=(x*vz-z*vx)/determinant,v=(z*ux-x*uz)/determinant;
  if(u<0||u>1||v<0||v>1)return false;
  return alpha[Math.min(127,Math.floor(v*128))*128+Math.min(127,Math.floor(u*128))]>8;
}
